import { Icon, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@amelio/ui-library';

import ProgressRing from './ProgressRing';

type ProgressRingColumnVariant = 'desktop' | 'mobile';

interface VariantConfig {
    /** Toggles this column at the module's own `md` breakpoint -- plain CSS in `app.css`, not
     * `hidden md:flex`, for the flat-cascade reason documented there. */
    toggleClassName: string;
    columnWidthClassName: string;
    verticalPaddingClassName: string;
    ringBoxSizeClassName: string;
    ringSize: number;
    dueDateOffsetClassName: string;
    dueDateTextSizeClassName: string;
    /** `Icon`'s `size` prop takes a preset key or a raw pixel number; the desktop column needs
     * 14px, between the `xs` (12px) and `sm` (16px) presets, so it passes the number directly
     * (an inline `width`/`height` style) instead of layering a `!h-… !w-…` override on top of a
     * preset that would only fight the library's own class in the cascade. */
    calendarIconSize: 'xs' | 14;
    /** Appended to `dataTestIdPrefix` -- empty for desktop (the original, unsuffixed id), so
     * every existing `objective-card-<id>-progress[-column]` lookup keeps working. */
    dataTestIdSuffix: string;
}

const VARIANT_CONFIG: Record<ProgressRingColumnVariant, VariantConfig> = {
    desktop: {
        toggleClassName: 'obj-ring-desktop',
        columnWidthClassName: 'w-[176px]',
        verticalPaddingClassName: 'pb-2',
        ringBoxSizeClassName: 'mt-1 h-[176px] w-[176px]',
        ringSize: 128,
        dueDateOffsetClassName: '-mt-6',
        dueDateTextSizeClassName: 'text-[12px]',
        calendarIconSize: 14,
        dataTestIdSuffix: '',
    },
    mobile: {
        toggleClassName: 'obj-ring-mobile',
        // The ring box states the 104px; the column only holds it to that as a floor, so the due
        // date under it -- which is wider than the ring at every locale we ship -- reads on one
        // line instead of breaking in two. `self-start` and not width alone: below `md` the card
        // is a column, whose items stretch, and a stretched column would centre the ring across
        // the whole card.
        columnWidthClassName: 'w-auto min-w-[104px] self-start',
        verticalPaddingClassName: 'pt-2',
        ringBoxSizeClassName: 'h-[104px] w-[104px]',
        ringSize: 77,
        dueDateOffsetClassName: '-mt-4',
        dueDateTextSizeClassName: 'text-[11px]',
        calendarIconSize: 'xs',
        dataTestIdSuffix: '-mobile',
    },
};

interface ProgressRingColumnProps {
    variant: ProgressRingColumnVariant;
    progression: number;
    overdue: boolean;
    /** Absent on a draft saved before its dates were chosen: the ring then stands alone. */
    formattedDueDate?: string;
    progressAriaLabel: string;
    dueDateTooltip?: string;
    /** e.g. `objective-card-${objective.id}` -- the variant's own suffix is appended to it. */
    dataTestIdPrefix: string;
    /** Added to the ring alone, never to the due date under it, which is text. */
    ringClassName?: string;
}

/**
 * One of `ObjectiveCard`'s two progress-ring columns: desktop (176px column, 128px ring) and
 * mobile (104px column, 77px ring) render the exact same content at different sizes, switched by
 * `.obj-ring-desktop` / `.obj-ring-mobile` -- only one is ever visible at a time.
 */
export default function ProgressRingColumn({
    variant,
    progression,
    overdue,
    formattedDueDate,
    progressAriaLabel,
    dueDateTooltip,
    dataTestIdPrefix,
    ringClassName = '',
}: ProgressRingColumnProps) {
    const config = VARIANT_CONFIG[variant];

    return (
        <div
            className={`${config.toggleClassName} ${config.columnWidthClassName} flex-shrink-0 flex-col items-center ${config.verticalPaddingClassName}`}
            data-testid={`${dataTestIdPrefix}-progress-column${config.dataTestIdSuffix}`}
        >
            <div
                className={`flex items-center justify-center ${config.ringBoxSizeClassName} ${ringClassName}`}
            >
                <ProgressRing
                    value={progression}
                    size={config.ringSize}
                    isOverdue={overdue}
                    ariaLabel={progressAriaLabel}
                    dataTestId={`${dataTestIdPrefix}-progress${config.dataTestIdSuffix}`}
                />
            </div>

            {formattedDueDate && (
                <TooltipProvider delayDuration={150}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <p
                                className={`${overdue ? 'text-error' : 'text-obj-slate'} ${config.dueDateOffsetClassName} flex items-center gap-1.5 whitespace-nowrap ${config.dueDateTextSizeClassName} font-medium`}
                            >
                                <Icon
                                    name={'time-calendar'}
                                    size={config.calendarIconSize}
                                    color={'inherit'}
                                />

                                {formattedDueDate}
                            </p>
                        </TooltipTrigger>

                        <TooltipContent
                            side={'top'}
                            className={'pointer-events-auto max-w-md'}
                        >
                            {dueDateTooltip}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    );
}
